/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, Dispatch, SetStateAction, useState } from 'react';
import { Box, ClickAwayListener } from '@mui/material';
import { trackNewChatClick } from '../../../../../../infra/analytics/utils';
import { QSButton, QSTypography } from '../../../../../../shared/components/atoms';
import CloseIcon from '../../../../../../shared/icons/Close';
import NewChatIcon from '../../../../../../shared/icons/icons-revamp/message';
import BroadcastGroupIcon from '../../../../../../shared/icons/icons-revamp/user_group';
import AddIcon from '../../../../../../shared/icons/icons-revamp/add';
import { isCreateGroupModalOpenAtom } from '../../../../../wa-groups/states/groups';
import { useSetAtom } from 'jotai';
import { useSelectedOrganization } from '../../../../../../shared/hooks/use-organizations';
import { useSubscribedPlan } from '../../../../../../modules/renew-plan/v1/hooks/use-subscribed-plan';
import { ProductPlan } from '../../../../../../modules/renew-plan/v1/typings';
import WAGroupsBlockerModal from './WAGroupsBlockerModal';

interface NewChatButtonProps {
  isNewChat: boolean;
  setNewChat: Dispatch<SetStateAction<boolean>>;
}

const NewChatButton: FC<NewChatButtonProps> = ({ isNewChat, setNewChat }) => {
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [isBlockerModalOpen, setIsBlockerModalOpen] = useState(false);
  const setIsCreateGroupModalOpen = useSetAtom(isCreateGroupModalOpenAtom);
  const { subscribedPlan } = useSubscribedPlan(false);

  const isStandardPlan = subscribedPlan?.id === ProductPlan.STANDARD;

  const handleFabToggle = () => {
    setIsFabMenuOpen((prev) => !prev);
  };

  const handleNewChatClick = () => {
    if (!isNewChat) {
      trackNewChatClick();
    }
    setNewChat(true);
    setIsFabMenuOpen(false);
  };

  const handleNewGroupClick = () => {
    if (isStandardPlan) {
      setIsBlockerModalOpen(true);
    } else {
      setIsCreateGroupModalOpen(true);
    }
    setIsFabMenuOpen(false);
  };

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setIsFabMenuOpen(false);
        }}
      >
        <Box
          css={css`
            position: absolute;
            right: 15px;
            bottom: 15px;
            z-index: 2;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 12px;
          `}
        >
          {isFabMenuOpen && (
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                gap: 12px;
              `}
            >
              <QSButton
                variant="contained"
                onClick={handleNewGroupClick}
                css={css`
                  margin: 0;
                  padding: 8px 12px;
                  border-radius: 4px;
                  display: flex;
                  align-items: center;
                  justify-content: flex-start;
                  box-shadow: 0px 8px 20px rgba(51, 96, 70, 0.18);
                `}
              >
                <BroadcastGroupIcon size={20} color="white" />
                <QSTypography
                  css={css`
                    margin-left: 8px;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 24px;
                    color: white;
                  `}
                >
                  New Group
                </QSTypography>
              </QSButton>
              <QSButton
                variant="contained"
                onClick={handleNewChatClick}
                css={css`
                  margin: 0;
                  padding: 8px 12px;
                  border-radius: 4px;
                  display: flex;
                  align-items: center;
                  justify-content: flex-start;
                  box-shadow: 0px 8px 20px rgba(51, 96, 70, 0.18);
                `}
              >
                <NewChatIcon size={20} />
                <QSTypography
                  css={css`
                    margin-left: 8px;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 24px;
                    color: white;
                  `}
                >
                  New Chat
                </QSTypography>
              </QSButton>
            </Box>
          )}
          <QSButton
            variant="contained"
            onClick={handleFabToggle}
            css={css`
              margin: 0;
              padding: ${isFabMenuOpen ? '0' : '8px 16px 8px 12px'};
              height: 40px;
              min-width: ${isFabMenuOpen ? '40px' : 'fit-content'};
              border-radius: ${isFabMenuOpen ? '50%' : '4px'};
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: ${isFabMenuOpen ? '0' : '10px'};
              box-shadow: ${isFabMenuOpen
                ? '0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 4px 6px -1px rgba(0, 0, 0, 0.10)'
                : 'none'};
              transition: all 160ms ease;
            `}
          >
            <>
              <AddIcon
                size={20}
                color="white"
                css={css`
                  transform: ${isFabMenuOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
                  transition: transform 160ms ease;
                `}
              />
              {!isFabMenuOpen && (
                <QSTypography
                  css={css`
                    font-size: 14px;
                    line-height: 24px;
                    font-weight: 700;
                    color: white;
                  `}
                >
                  New
                </QSTypography>
              )}
            </>
          </QSButton>
        </Box>
      </ClickAwayListener>
      {isBlockerModalOpen && (
        <WAGroupsBlockerModal open={isBlockerModalOpen} onClose={() => setIsBlockerModalOpen(false)} />
      )}
    </>
  );
};

export default NewChatButton;
